import { useState, useEffect, useRef } from 'react';
import { Database, Upload, Plus, FileText, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { getDataSources, uploadCSV } from '../lib/api';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

export const DataSources = () => {
    const [dataSources, setDataSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        loadDataSources();
    }, []);

    const loadDataSources = async () => {
        try {
            setLoading(true);
            const response = await getDataSources();
            setDataSources(response.data);
        } catch (error) {
            console.error('Failed to load data sources:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.csv')) {
            toast.error('Please upload a CSV file');
            return;
        }

        try {
            setUploading(true);
            await uploadCSV(file);
            toast.success('CSV uploaded successfully!');
            setDialogOpen(false);
            loadDataSources();
        } catch (error) {
            console.error('Failed to upload CSV:', error);
            toast.error('Failed to upload CSV file');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="data-sources-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-card-foreground flex items-center gap-3">
                        <Database className="h-8 w-8 text-primary" />
                        Data Sources
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Manage your data connections
                    </p>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="btn-glow" data-testid="add-data-source-btn">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Data Source
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-border">
                        <DialogHeader>
                            <DialogTitle className="text-card-foreground">Add Data Source</DialogTitle>
                            <DialogDescription>
                                Upload a CSV file to import your data
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-6 pt-4">
                            {/* CSV Upload */}
                            <div className="space-y-4">
                                <Label className="text-card-foreground">Upload CSV File</Label>
                                <div 
                                    className={cn(
                                        "border-2 border-dashed border-border rounded-lg p-8 text-center",
                                        "hover:border-primary/50 transition-colors cursor-pointer"
                                    )}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                                    <p className="text-card-foreground mb-2">
                                        Click to upload or drag and drop
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        CSV files only
                                    </p>
                                    <Input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv"
                                        className="hidden"
                                        onChange={handleFileUpload}
                                        data-testid="csv-file-input"
                                    />
                                </div>
                                {uploading && (
                                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                        <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                                        Uploading...
                                    </div>
                                )}
                            </div>

                            {/* CSV Format Info */}
                            <div className="bg-background/50 rounded-lg p-4">
                                <p className="text-sm font-medium text-card-foreground mb-2">Expected CSV Format:</p>
                                <code className="text-xs text-muted-foreground block">
                                    date, category, region, channel, revenue, orders, visitors, customers, churned_customers, nps_score
                                </code>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Data Sources List */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-32" />
                    ))}
                </div>
            ) : dataSources.length === 0 ? (
                <Card className="bg-card border-border">
                    <CardContent className="py-12 text-center">
                        <Database className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                        <p className="text-lg text-card-foreground mb-2">No data sources configured</p>
                        <p className="text-muted-foreground mb-6">
                            Upload a CSV file or generate demo data from the Dashboard
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {dataSources.map((source) => (
                        <Card 
                            key={source.id}
                            className="bg-card border-border hover:border-primary/30 transition-colors"
                            data-testid={`data-source-${source.id}`}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-primary/10">
                                            <FileText className="h-6 w-6 text-primary" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-card-foreground">{source.name}</h3>
                                            <p className="text-sm text-muted-foreground">
                                                Created {format(new Date(source.created_at), 'MMM d, yyyy')}
                                            </p>
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="text-xs capitalize">
                                        {source.source_type}
                                    </Badge>
                                </div>
                                
                                {source.config && (
                                    <div className="mt-4 pt-4 border-t border-border">
                                        <p className="text-xs text-muted-foreground">
                                            {source.config.generated ? 
                                                `Generated dataset (${source.config.days} days)` :
                                                source.config.filename || 'Custom configuration'
                                            }
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default DataSources;
